// api
import {
  useEnergyDataSpecificEnergyDataSpecificAccountIdEnergyGroupIdGetQuery,
  useEnergyTimestampsSummaryEnergyTimestampsSummaryAccountIdEnergyGroupIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/energyData';
import type { GetEnergyGroupDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// relative
import { calculateTrendPercentage } from '../utils/dateRangeCalculator';

// external
import { DateTime } from 'luxon';

export interface EnergyTrendData {
  /** Total energy for last 30 days */
  last30DaysTotal: number | null;
  /** Total energy for the 30 days before that */
  previous30DaysTotal: number | null;
  /** Trend percentage (negative = decrease, positive = increase) */
  trendPercentage: number | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether an error occurred */
  error: unknown;
}

/**
 * Hook to fetch 60 days of energy data and calculate trend
 * Assumes the root node ID is the energy group ID itself (or you can pass it separately)
 */
export const useEnergyDeepDiveTrend = (
  accountId: number | string | undefined,
  group: GetEnergyGroupDto | undefined,
): EnergyTrendData => {
  const energyGroupId = group?.energyGroupId;
  const rootNodeId = group?.energyTreeNodes?.[0]?.energyTreeNodeId;

  // Fetch timestamp summary to verify data availability
  const {
    data: timestampSummary,
    isLoading: isLoadingTimestamps,
    error: timestampError,
  } = useEnergyTimestampsSummaryEnergyTimestampsSummaryAccountIdEnergyGroupIdGetQuery(
    {
      accountId: accountId ? Number(accountId) : 0,
      energyGroupId: energyGroupId || 0,
    },
    {
      skip: !accountId || !energyGroupId || group?.options?.state === 'Error',
    },
  );

  const latestDataTimestamp = timestampSummary?.minLatestDataTimestamp;
  const latestDataDate = latestDataTimestamp
    ? DateTime.fromISO(latestDataTimestamp)
    : null;
  const hasValidLatestDataDate = Boolean(latestDataDate?.isValid);
  const rangeEndDate = hasValidLatestDataDate ? latestDataDate! : null;
  const rangeStartDate = rangeEndDate?.minus({ days: 60 }) ?? null;
  const last4WeeksStartDate = rangeEndDate?.minus({ days: 30 }) ?? null;

  // Fetch 60 days of data for the root node (node ID is typically the energy group ID)
  const {
    data: energyData,
    isLoading: isLoadingEnergyData,
    error: energyDataError,
  } = useEnergyDataSpecificEnergyDataSpecificAccountIdEnergyGroupIdGetQuery(
    {
      accountId: accountId ? Number(accountId) : 0,
      energyGroupId: energyGroupId || 0,
      startTimestamp: rangeStartDate?.toISO() ?? '',
      endTimestamp: rangeEndDate?.toISO() ?? '',
      nodeIds: rootNodeId ? [rootNodeId] : [],
    },
    {
      skip:
        !accountId ||
        !energyGroupId ||
        !rootNodeId ||
        !hasValidLatestDataDate ||
        !rangeStartDate ||
        !rangeEndDate ||
        !last4WeeksStartDate,
    },
  );

  const trendData = (() => {
    if (
      !energyData ||
      !rangeStartDate ||
      !rangeEndDate ||
      !last4WeeksStartDate
    ) {
      return {
        last30DaysTotal: null,
        previous30DaysTotal: null,
        trendPercentage: null,
      };
    }

    const last4WeeksData = energyData.EnergyData.filter((point) => {
      const pointDate = DateTime.fromISO(point.timestamp);

      return (
        pointDate.isValid &&
        pointDate >= last4WeeksStartDate &&
        pointDate <= rangeEndDate
      );
    });

    const weeks4To8Data = energyData.EnergyData.filter((point) => {
      const pointDate = DateTime.fromISO(point.timestamp);

      return (
        pointDate.isValid &&
        pointDate >= rangeStartDate &&
        pointDate < last4WeeksStartDate
      );
    });

    const last30DaysTotal = last4WeeksData.reduce(
      (sum, point) => sum + point.sum,
      0,
    );
    const previous30DaysTotal = weeks4To8Data.reduce(
      (sum, point) => sum + point.sum,
      0,
    );

    return {
      last30DaysTotal: last30DaysTotal || null,
      previous30DaysTotal: previous30DaysTotal || null,
      trendPercentage: calculateTrendPercentage(
        last30DaysTotal,
        previous30DaysTotal,
      ),
    };
  })();

  return {
    ...trendData,
    isLoading: isLoadingTimestamps || isLoadingEnergyData,
    error: timestampError || energyDataError || null,
  };
};
